import { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/context/AuthContext';
import { API, getBackendBaseUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  Settings,
  User,
  Mail,
  MapPin,
  Building,
  Camera,
  Save,
  FlaskConical,
  CreditCard
} from 'lucide-react';

export default function SettingsPage() {
  const { setUserData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    cpf: '',
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'Brasil',
    notification_summary_email: true,
    notification_support_email: true,
    notification_billing_email: true,
    sandbox_mode: false,
    picture: '',
  });
  const [billingHeader, setBillingHeader] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/profile`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Falha ao carregar perfil');
      }
      const data = await response.json();
      setForm((current) => ({
        ...current,
        ...data.user,
        picture: data.user?.picture || '',
        email: data.user?.email || '',
      }));
      setAvatarPreview(data.user?.picture || '');
      setBillingHeader(data.billing || null);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5 MB');
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      [
        'name',
        'phone',
        'cpf',
        'company_name',
        'address_line1',
        'address_line2',
        'city',
        'state',
        'zip_code',
        'country',
      ].forEach((field) => {
        formData.append(field, form[field] || '');
      });
      ['notification_summary_email', 'notification_support_email', 'notification_billing_email'].forEach((field) => {
        formData.append(field, String(Boolean(form[field])));
      });
      formData.append('sandbox_mode', String(Boolean(form.sandbox_mode)));
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      const response = await fetch(`${API}/profile`, {
        method: 'PUT',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível salvar as configurações');
      }

      const updatedUser = data.user || {};
      setForm((current) => ({
        ...current,
        ...updatedUser,
        picture: updatedUser.picture || current.picture,
      }));
      setAvatarFile(null);
      setAvatarPreview(updatedUser.picture || avatarPreview);
      setBillingHeader(data.billing || billingHeader);
      setUserData({
        user_id: updatedUser.user_id,
        email: updatedUser.email,
        name: updatedUser.name,
        picture: updatedUser.picture,
        role: updatedUser.role,
        plan: (data.billing?.plan) || updatedUser.plan,
        plan_status: (data.billing?.plan_status) || updatedUser.plan_status,
        billing_cycle: data.billing?.billing_cycle,
        trial_ends_at: data.billing?.trial_ends_at,
        days_until_trial_ends: data.billing?.days_until_trial_ends,
      });
      toast.success(data.message || 'Configurações salvas');
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 md:p-8 max-w-6xl mx-auto">
          <div className="space-y-6 animate-pulse">
            <div className="h-10 w-64 bg-zinc-200 rounded-xl" />
            <div className="h-80 rounded-2xl bg-zinc-200" />
          </div>
        </div>
      </AppLayout>
    );
  }

  const isTrialing = billingHeader?.plan_status === 'trialing' && billingHeader?.trial_ends_at;
  const trialLabel = isTrialing
    ? billingHeader?.days_until_trial_ends <= 0
      ? 'Teste termina hoje'
      : `Teste termina em ${billingHeader?.days_until_trial_ends} dia(s)`
    : null;

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 flex items-center gap-3">
            <Settings className="w-8 h-8 text-violet-600" />
            Configurações
          </h1>
          <p className="text-zinc-500 mt-1">
            Atualize seus dados de cadastro, foto e preferências da conta.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="border-zinc-200 lg:col-span-1">
            <CardHeader>
              <CardTitle>Perfil</CardTitle>
              <CardDescription>Foto e dados principais da conta.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isTrialing ? (
                <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-sky-900">{trialLabel}</p>
                      <p className="text-xs text-sky-700 mt-1">
                        Primeira cobrança prevista para {new Date(billingHeader.first_charge_date || billingHeader.trial_ends_at).toLocaleDateString('pt-BR')}.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="flex flex-col items-center gap-4">
                <Avatar className="w-28 h-28">
                  <AvatarImage src={avatarPreview || form.picture} />
                  <AvatarFallback className="bg-violet-100 text-violet-700 text-3xl">
                    {form.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
                <Button variant="outline" className="rounded-full" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2" />
                  Alterar foto
                </Button>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input id="name" value={form.name || ''} onChange={(event) => updateField('name', event.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email cadastrado</Label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input id="email" value={form.email || ''} disabled className="pl-9 bg-zinc-50" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input id="phone" value={form.phone || ''} onChange={(event) => updateField('phone', event.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF</Label>
                  <Input id="cpf" value={form.cpf || ''} onChange={(event) => updateField('cpf', event.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-6">
            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-violet-600" />
                  Dados de endereço
                </CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="address_line1">Endereço</Label>
                  <Input id="address_line1" value={form.address_line1 || ''} onChange={(event) => updateField('address_line1', event.target.value)} />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="address_line2">Complemento</Label>
                  <Input id="address_line2" value={form.address_line2 || ''} onChange={(event) => updateField('address_line2', event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Cidade</Label>
                  <Input id="city" value={form.city || ''} onChange={(event) => updateField('city', event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">Estado</Label>
                  <Input id="state" value={form.state || ''} onChange={(event) => updateField('state', event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">CEP</Label>
                  <Input id="zip_code" value={form.zip_code || ''} onChange={(event) => updateField('zip_code', event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">País</Label>
                  <Input id="country" value={form.country || ''} onChange={(event) => updateField('country', event.target.value)} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-violet-600" />
                  Informações adicionais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Empresa / Organização</Label>
                  <Input id="company_name" value={form.company_name || ''} onChange={(event) => updateField('company_name', event.target.value)} />
                </div>

                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-4">
                    <div>
                      <p className="font-medium text-zinc-900">Resumo por email</p>
                      <p className="text-sm text-zinc-500">Receber avisos e recados sobre resumos.</p>
                    </div>
                    <Switch checked={Boolean(form.notification_summary_email)} onCheckedChange={(checked) => updateField('notification_summary_email', checked)} />
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-4">
                    <div>
                      <p className="font-medium text-zinc-900">Atualizações de suporte</p>
                      <p className="text-sm text-zinc-500">Receber novidades sobre tickets e atendimento.</p>
                    </div>
                    <Switch checked={Boolean(form.notification_support_email)} onCheckedChange={(checked) => updateField('notification_support_email', checked)} />
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-4">
                    <div>
                      <p className="font-medium text-zinc-900">Cobrança e faturas</p>
                      <p className="text-sm text-zinc-500">Receber lembretes de vencimento e pagamentos.</p>
                    </div>
                    <Switch checked={Boolean(form.notification_billing_email)} onCheckedChange={(checked) => updateField('notification_billing_email', checked)} />
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-4">
                    <div className="pr-4">
                      <p className="font-medium text-zinc-900 flex items-center gap-2">
                        <FlaskConical className="w-4 h-4 text-violet-600" />
                        Sandbox Mode
                      </p>
                      <p className="text-sm text-zinc-500">
                        Quando ativo, sua conta continua usando o ambiente de testes. Desative para sair do modo sandbox.
                      </p>
                    </div>
                    <Switch checked={Boolean(form.sandbox_mode)} onCheckedChange={(checked) => updateField('sandbox_mode', checked)} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button className="rounded-full" onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : 'Salvar configurações'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
